import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type YearFilter = number | "S" | "all";

export type TimesheetStatusFilter =
  | "all"
  | "draft"
  | "ready"
  | "signed"
  | "archived"
  | "invoice_draft"
  | "invoice_created"
  | "invoice_paid";

export interface TimesheetFilters {
  q: string;
  jahr: YearFilter;
  kunde: string;
  ordner: string;
  sordner: string;
  status: TimesheetStatusFilter;
  billing: string;
  rueckfrage: boolean;
  /** Wird gesetzt, wenn die Suche über Contact-Aging ausgelöst wurde. */
  contactAgingSearch: boolean;
}

const DEFAULT_YEAR = new Date().getFullYear();

const DEFAULTS: TimesheetFilters = {
  q: "",
  jahr: DEFAULT_YEAR,
  kunde: "",
  ordner: "",
  sordner: "",
  status: "all",
  billing: "all",
  rueckfrage: false,
  contactAgingSearch: false,
};

function parseYear(raw: string | null): YearFilter {
  if (!raw) return DEFAULTS.jahr;
  const value = raw.trim();
  if (!value) return DEFAULTS.jahr;
  if (value.toLowerCase() === "all") return "all";
  if (value.toUpperCase() === "S") return "S";
  const num = Number(value);
  return Number.isFinite(num) ? num : DEFAULTS.jahr;
}

function parseStatus(raw: string | null): TimesheetStatusFilter {
  const value = (raw || "").toLowerCase();
  const allowed: TimesheetStatusFilter[] = [
    "all",
    "draft",
    "ready",
    "signed",
    "archived",
    "invoice_draft",
    "invoice_created",
    "invoice_paid",
  ];
  return allowed.includes(value as TimesheetStatusFilter)
    ? (value as TimesheetStatusFilter)
    : "all";
}

function serializeYear(value: YearFilter): string {
  if (value === "all") return "all";
  if (value === "S") return "S";
  return String(value);
}

export function timesheetHasActiveFilters(f: TimesheetFilters): boolean {
  return (
    !!f.q.trim() ||
    f.jahr !== DEFAULT_YEAR ||
    !!f.kunde ||
    !!f.ordner ||
    !!f.sordner ||
    f.status !== "all" ||
    (f.billing !== "all" && !!f.billing) ||
    f.rueckfrage
  );
}

function filtersFromSearchParams(searchParams: URLSearchParams): TimesheetFilters {
  return {
    q: searchParams.get("q") ?? "",
    jahr: parseYear(searchParams.get("jahr")),
    kunde: searchParams.get("kunde") ?? "",
    ordner: searchParams.get("ordner") ?? "",
    sordner: searchParams.get("sordner") ?? "",
    status: parseStatus(searchParams.get("status")),
    billing: searchParams.get("billing") ?? "all",
    rueckfrage: searchParams.get("rueckfrage") === "1",
    contactAgingSearch: searchParams.get("contactAging") === "1",
  };
}

export function useTimesheetFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TimesheetFilters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );

  const update = useCallback(
    (patch: Partial<TimesheetFilters>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const merged = { ...filtersFromSearchParams(prev), ...patch };

        const setOrDelete = (key: string, value: string, omit: boolean) => {
          if (omit) next.delete(key);
          else next.set(key, value);
        };

        setOrDelete("q", merged.q, !merged.q.trim());

        const defaultJahr = DEFAULT_YEAR;
        setOrDelete(
          "jahr",
          serializeYear(merged.jahr),
          merged.jahr === defaultJahr,
        );

        setOrDelete("kunde", merged.kunde, !merged.kunde);
        setOrDelete("ordner", merged.ordner, !merged.ordner);
        setOrDelete("sordner", merged.sordner, !merged.sordner);
        setOrDelete("status", merged.status, merged.status === "all");
        setOrDelete("billing", merged.billing, !merged.billing || merged.billing === "all");
        if (merged.rueckfrage) next.set("rueckfrage", "1");
        else next.delete("rueckfrage");
        if (merged.contactAgingSearch) next.set("contactAging", "1");
        else next.delete("contactAging");

        if (patch.jahr !== undefined) {
          next.delete("kunde");
          next.delete("ordner");
          next.delete("sordner");
        }
        if (patch.kunde !== undefined) {
          next.delete("ordner");
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return { filters, update, reset, defaultYear: DEFAULT_YEAR };
}
